import React from 'react';
import PropTypes from 'prop-types';
import Calendar from 'rc-calendar/lib';
import Picker from 'rc-calendar/lib/Picker';
import TimePickerPanel from 'rc-time-picker/lib/Panel';
import en from 'rc-calendar/lib/locale/en_US';
import ru from 'rc-calendar/lib/locale/ru_RU';
import { isFunction } from 'lodash';
import moment from 'moment';
import i18next from 'i18next';
import clxBind from 'classnames/bind';
import MonthCalendar from './MonthCalendar';
import YearCalendar from './YearCalendar';
import Input from '../Input';
import Button from '../Button';
import styles from './styles.module.scss';
import './styles.scss';

const locales = { ru, en };

const formats = {
  time: 'HH:mm:ss',
  date: 'L',
  month: 'MM.YYYY',
  year: 'YYYY',
};

const clx = clxBind.bind(styles);

const timePickerElement = <TimePickerPanel defaultValue={moment('00:00:00', formats.time)} />;

export default class DateTimePicker extends React.Component {
  static propTypes = {
    type: PropTypes.oneOf(['date', 'month', 'year']),
    mode: PropTypes.oneOf(['info', 'success', 'danger', 'warning']),
    inputProps: PropTypes.object,
    value: PropTypes.any,
    defaultValue: PropTypes.any,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    showClear: PropTypes.bool,
    showTime: PropTypes.bool,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    className: PropTypes.string,
  };

  static defaultProps = {
    type: 'date',
    mode: undefined,
    inputProps: {},
    value: '',
    defaultValue: undefined,
    disabled: false,
    readOnly: false,
    showClear: true,
    showTime: false,
    onChange: undefined,
    onFocus: undefined,
    onBlur: undefined,
    className: undefined,
  };

  constructor(props) {
    super(props);

    this.value = '';
    this.isFocused = false;
    this.state = { open: false };
  }

  handleContainerRef = ref => (this.container = ref);

  getContainer = () => this.container;

  getPickerValue = () => {
    const value = moment(this.props.value);
    return value.isValid() ? value : undefined;
  };

  toggle = open => {
    const { readOnly, disabled } = this.props;

    if (readOnly || disabled) {
      return;
    }

    this.setState(state => {
      const toBeOpen = open ?? !state.open;

      if (!toBeOpen) {
        this.handleBlur();
      }

      return {
        open: toBeOpen,
      };
    });
  };

  open = () => {
    this.toggle(true);
  };

  close = () => {
    this.toggle(false);
  };

  clear = () => {
    this.close();
    this.handleChange();
  };

  handleChange = event => {
    const { readOnly, disabled, onChange } = this.props;

    if (readOnly || disabled) {
      return;
    }

    onChange(event);
  };

  handleTab = event => {
    const { keyCode } = event;

    switch (keyCode) {
      case 9:
        this.close();
        break;
      default:
        break;
    }
  };

  handleFocus = () => {
    const { onFocus } = this.props;

    if (this.isFocused) {
      return;
    }

    if (isFunction(onFocus)) {
      onFocus();
    }

    this.isFocused = true;
  };

  handleBlur = () => {
    const { onBlur } = this.props;

    setImmediate(() => {
      if (this.container.contains(document.activeElement) || !this.isFocused) {
        return;
      }

      if (isFunction(onBlur)) {
        onBlur(this.value);
      }

      this.isFocused = false;
    });
  };

  handleToggle = () => this.toggle();

  renderClear = () => {
    const { showClear, readOnly, disabled } = this.props;

    if (!showClear || !this.getPickerValue() || readOnly || disabled) {
      return null;
    }

    return (
      <div className={styles['button-cell']}>
        <Button
          border="none"
          icon="times"
          disabled={disabled || readOnly}
          onClick={this.clear}
          className={styles['button']}
        />
      </div>
    );
  };

  renderToggle = () => {
    const { readOnly, disabled } = this.props;

    return (
      <div className={styles['button-cell']}>
        <Button
          border="none"
          icon="calendar"
          disabled={disabled || readOnly}
          onClick={this.handleToggle}
          className={styles['button']}
        />
      </div>
    );
  };

  render() {
    const { open } = this.state;
    const {
      type,
      mode,
      inputProps,
      defaultValue,
      disabled,
      readOnly,
      showClear,
      showTime,
      className,
      ...rest
    } = this.props;

    let CalendarType;

    switch (type) {
      case 'year':
        CalendarType = YearCalendar;
        break;
      case 'month':
        CalendarType = MonthCalendar;
        break;
      default:
        CalendarType = Calendar;
    }

    let format = formats[type] ?? formats.date;

    if (showTime && type !== 'time') {
      format += ` ${formats.time}`;
    }

    const calendar = (
      <CalendarType
        dateInputPlaceholder={i18next.t('date-input.placeholder')}
        locale={locales[i18next.language] ?? ru}
        showDateInput={showClear}
        timePicker={showTime ? timePickerElement : null}
        format={format}
        {...rest}
      />
    );

    return (
      <div
        className={styles['container']}
        onKeyDown={this.handleTab}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        tabIndex={-1}
        role="tab"
      >
        <Picker
          calendar={calendar}
          getCalendarContainer={this.getContainer}
          align={{ points: ['tl', 'bl'], offset: [0, 4] }}
          value={this.getPickerValue()}
          defaultValue={defaultValue}
          onChange={this.handleChange}
          onOpenChange={this.toggle}
          disabled={disabled || readOnly}
          open={open}
        >
          {({ value: inputValue }) => {
            const inputClasses = clx(className, 'input');

            this.value = inputValue;

            return (
              <div className={styles['input-cell']}>
                <Input
                  className={inputClasses}
                  value={inputValue ? inputValue.format(format) : ''}
                  disabled={disabled}
                  mode={mode}
                  readOnly
                  {...inputProps}
                />
                <div ref={this.handleContainerRef} />
              </div>
            );
          }}
        </Picker>
        {this.renderClear()}
        {this.renderToggle()}
      </div>
    );
  }
}
